// Zone-aware conversions for the event settings form (SCRUM-334). Mirrors the
// API's `event-time.ts` (same two-pass offset algorithm, same DST rules) so a
// value typed in the admin form and the value the API derives from it agree.
//
// Nothing here has any built-in notion of Central time — every function takes
// an IANA zone name and does the same Intl-based math for any zone.

export const DEFAULT_TIMEZONE = Intl.DateTimeFormat().resolvedOptions().timeZone;

const ONE_HOUR_MS = 60 * 60 * 1000;

const pad2 = (n) => String(n).padStart(2, '0');

// Formats an instant's wall-clock parts IN a given zone, then reinterprets
// those parts AS IF they were UTC — this is the standard trick for getting a
// zone's offset at a given instant without a tz database dependency: the
// difference between this "wall-as-UTC" value and the real instant is the
// zone's offset (in ms) at that instant.
function tzWallMs(instantMs, timeZone) {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
  const parts = dtf.formatToParts(new Date(instantMs));
  const get = (type) => Number(parts.find((p) => p.type === type).value);
  let hour = get('hour');
  if (hour === 24) hour = 0; // some ICU builds print midnight as "24" under h23
  return Date.UTC(get('year'), get('month') - 1, get('day'), hour, get('minute'), get('second'));
}

// Offset (ms) to ADD to an instant to get its local wall-clock time in `timeZone`.
function offsetAt(instantMs, timeZone) {
  return tzWallMs(instantMs, timeZone) - instantMs;
}

export function timeZoneOptions() {
  return Intl.supportedValuesOf('timeZone');
}

// "America/Chicago (CDT, UTC−5)" — the abbreviation and offset as of `at`
// (offsets change across DST, so the label is always evaluated at a moment).
export function zoneLabel(tz, at = new Date()) {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    timeZoneName: 'short',
  });
  const abbrPart = dtf.formatToParts(at).find((p) => p.type === 'timeZoneName');
  const abbr = abbrPart ? abbrPart.value : tz;
  const offsetMin = Math.round(offsetAt(at.getTime(), tz) / 60000);
  const sign = offsetMin < 0 ? '−' : '+';
  const absMin = Math.abs(offsetMin);
  const hours = Math.floor(absMin / 60);
  const mins = absMin % 60;
  const offsetLabel = mins === 0 ? `UTC${sign}${hours}` : `UTC${sign}${hours}:${pad2(mins)}`;
  return `${tz} (${abbr}, ${offsetLabel})`;
}

// Instant (ISO string) -> local wall-clock parts in `tz`.
export function instantToLocalParts(iso, tz) {
  const instantMs = new Date(iso).getTime();
  const wallMs = tzWallMs(instantMs, tz);
  const d = new Date(wallMs);
  const ymd = `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`;
  const hm = `${pad2(d.getUTCHours())}:${pad2(d.getUTCMinutes())}`;
  return { ymd, hm };
}

// Local wall-clock parts (`ymd` "YYYY-MM-DD", `hm` "HH:mm") in `tz` -> instant
// (ISO string), DST-correct.
//
// wall = the parts read as if they were already UTC.
// guess = a first-pass instant, correcting `wall` by the offset `wall` itself
//   would have if IT were the instant (a good first approximation).
// candidate = a second pass, correcting by the offset AT guess.
// If candidate's own offset agrees with guess's offset, the wall-clock time
//   unambiguously exists at `candidate` — accept it. Otherwise the wall-clock
//   time falls in a gap that doesn't exist (e.g. spring-forward); keep `guess`
//   — resolves to the instant the two-pass offset lookup converges on, not a
//   fixed "always forward" direction (which one that is depends on the
//   zone's offset sign). Parity with the API's own `event-time.ts`.
// Finally: a fall-back hour is ambiguous (the same wall-clock time occurs
//   twice, an hour apart, at two different offsets) — if resolved minus one
//   hour lands back on the same wall-clock time with an offset one hour
//   larger, that is the earlier of the two occurrences; prefer it.
export function localPartsToInstant(ymd, hm, tz) {
  const [y, mo, d] = ymd.split('-').map(Number);
  const [h, mi] = hm.split(':').map(Number);
  const wall = Date.UTC(y, mo - 1, d, h, mi, 0, 0);

  const guess = wall - offsetAt(wall, tz);
  const candidateOffset = offsetAt(guess, tz);
  const candidate = wall - candidateOffset;

  let resolved = offsetAt(candidate, tz) === candidateOffset ? candidate : guess;

  const earlier = resolved - ONE_HOUR_MS;
  if (tzWallMs(earlier, tz) === wall && offsetAt(earlier, tz) === offsetAt(resolved, tz) + ONE_HOUR_MS) {
    resolved = earlier;
  }

  return new Date(resolved).toISOString();
}

// "YYYY-MM-DD" -> "YYYY-MM-DD", `deltaDays` calendar days later (negative to
// go back). Pure calendar-day arithmetic — no timezone involved, matching
// how an operator reads "1 day before the start": the same wall-clock time,
// the previous calendar day, in whatever zone the event already uses.
// UTC-math on the wall-clock parts is safe here (same trick `endOfLocalDay`
// uses below) since we're never treating the result as an instant.
export function shiftYmd(ymd, deltaDays) {
  const [y, mo, d] = ymd.split('-').map(Number);
  const shifted = new Date(Date.UTC(y, mo - 1, d + deltaDays));
  return `${shifted.getUTCFullYear()}-${pad2(shifted.getUTCMonth() + 1)}-${pad2(shifted.getUTCDate())}`;
}

// 23:59:59.999 local time, `plusDays` days after the local calendar day of `iso`.
export function endOfLocalDay(iso, tz, plusDays = 0) {
  const { ymd } = instantToLocalParts(iso, tz);
  // Advance to the START of the day AFTER the target day, resolve that
  // through the DST-correct conversion, then step back 1ms to land on
  // 23:59:59.999 of the target day.
  const nextDayYmd = shiftYmd(ymd, plusDays + 1);
  const nextDayStartMs = new Date(localPartsToInstant(nextDayYmd, '00:00', tz)).getTime();
  return new Date(nextDayStartMs - 1).toISOString();
}

// Human display, mirroring the API's `formatInZone`. `style` is 'datetime'
// (default), 'date', or 'time'.
export function formatInZone(iso, tz, style = 'datetime') {
  const date = new Date(iso);
  if (style === 'date') {
    return new Intl.DateTimeFormat('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      timeZone: tz,
    }).format(date);
  }
  if (style === 'time') {
    return new Intl.DateTimeFormat('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      timeZoneName: 'short',
      timeZone: tz,
    }).format(date);
  }
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
    timeZone: tz,
  }).format(date);
}
